"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_CREATIVE_PERFORMANCE_VIEWS = exports.DEFAULT_CREATIVE_PERFORMANCE_VIEW_SCHEMA_VERSION = void 0;
/** Saved Creative-Performance-view payload version understood by the current web deserializer. */
exports.DEFAULT_CREATIVE_PERFORMANCE_VIEW_SCHEMA_VERSION = 1;
/**
 * The five standard Creative Performance views — the CANONICAL DEFINITION SOURCE.
 *
 * This is the SEED SOURCE for per-brand `CreativePerformanceView` rows: `seedDefaults`
 * (`creative-performance-view-api-service.ts`) inserts these at brand creation, and a data
 * migration backfilled them for every existing brand. The rows are user-owned and editable
 * afterward — rename / delete / update like any saved view — so editing a definition here reaches
 * only NEW brands; existing brands keep the rows they were seeded with. Array order is sidebar
 * order: seeding backdates `createdAt` to epoch + index seconds so `ORDER BY createdAt ASC`
 * preserves this order ahead of any user-created view.
 *
 * It is also the reference the API-side predicate pin copies from
 * (`creative-performance-analysis.test.ts` decodes Fatigued Ads verbatim from here).
 *
 * Each query is CANONICAL — byte-identical to what `toCreativePerformanceParams` writes for the state
 * it names, so a seeded default and a hand-built view compare equal. `creative-performance-preset-state.test.ts`
 * pins that; correct a string against the test, never the test against a string.
 *
 * Both views sort by spend descending, which IS the surface default, so neither carries `sort`/`dir`.
 * Both set `view=table` because a column list is only rendered by the table.
 *
 * ALL FIVE specified Insights views now ship, alongside the five Research ones.
 *
 * Two predicates from the original spec are deliberately not bounds. Top Landing Pages' ">= 300
 * outbound clicks" is a property of the landing PAGE, and filters run per creative before grouping;
 * Fatigued Ads' "frequency > 2.5" would silently empty the view on the ~97% of accounts the
 * de-duplicated-reach lane does not cover. Both are columns instead, and both views' ordering already
 * does the work the floors were for.
 *
 * A view that loses a COLUMN still answers the question it names; one that loses its defining FILTER
 * answers a different question under the same name, so those ship when their capability does.
 *
 * BOTH SHIPPED VIEWS NOW CARRY EVERY COLUMN THEIR SPEC ASKED FOR. Top Spenders reads share of
 * ACCOUNT — the view ranks across the whole account, so "this creative took 15% of account spend" is
 * the reading its ranking implies; share of ad set and of campaign remain available as columns a user
 * can add.
 *
 * One PREDICATE is still missing on Top Landing Pages: its specified ">= 300 outbound clicks" floor
 * is a property of the landing PAGE, and filters run per creative BEFORE grouping, so a creative-level
 * `outboundClicks >= 300` would drop individual creatives rather than thin destinations. Group-level
 * filtering is its own capability. Spend-descending group order already puts the destinations that
 * matter at the top, which is what the floor was for. See `openspec/changes/add-creative-performance-default-views/design.md`.
 */
exports.DEFAULT_CREATIVE_PERFORMANCE_VIEWS = [
    {
        name: 'Top Spenders',
        data: {
            schemaVersion: exports.DEFAULT_CREATIVE_PERFORMANCE_VIEW_SCHEMA_VERSION,
            // No grouping: the grid's base row already IS a unique creative, so `group=uniqueCreative`
            // would add an outline level holding exactly one row each.
            query: 'range=30d&view=table&columns=spend%2CspendShareOfAccount%2Cresults%2Ccpa%2Croas%2CoutboundCtr%2CdaysActive%2CadCount',
        },
    },
    {
        name: 'Latest Launches',
        data: {
            schemaVersion: exports.DEFAULT_CREATIVE_PERFORMANCE_VIEW_SCHEMA_VERSION,
            /**
             * `daysActive <= 14` IS "first delivered within the last 14 days" — no relative-date
             * operator on `launchDate` was needed after all, because days-active is already measured
             * from the earliest member launch against now, so the window rolls on its own.
             *
             * `range=14d` is the surface default and therefore absent from the query. Grouped by
             * launch day, newest cohort first, creatives within a day ranked by spend.
             *
             * A creative whose launch date is unknown reports no days-active, and a null fails the
             * bound — so it is excluded, which is right: "Latest Launches" should not list an ad
             * nobody can date.
             */
            query: 'view=table&filters=eyJtIjoiYSIsInIiOlt7ImYiOiJkYXlzQWN0aXZlIiwibyI6Imx0ZSIsInYiOjE0fV19&group=launchDate&groupOrderDir=desc&columns=launchDate%2CdaysActive%2Cspend%2CspendShareOfCampaign%2ChookRate%2CoutboundCtr%2CcostPerAddToCart%2CcpaVsTarget%2Croas',
        },
    },
    {
        name: 'Top Creatives',
        data: {
            schemaVersion: exports.DEFAULT_CREATIVE_PERFORMANCE_VIEW_SCHEMA_VERSION,
            /**
             * The specified floor is `spend >= max(2x target CPA, account median spend-per-creative)`
             * with `results >= 5`. `max(a, b)` is just both bounds AND-ed, so no max concept was
             * needed — and the median half is now a set-relative operator, computed over the whole
             * unfiltered window before any row is dropped.
             *
             * The `results >= 5` half is an ADAPTIVE floor, not a fixed one: it applies
             * `min(5, the 10th-ranked creative's own count)`. Five is right for a purchase account
             * — median creatives clearing it is 13 — and wrong for one whose whole distribution
             * sits below it. Measured on a live subscription account, 223 creatives shared 54
             * conversions over 60 days and exactly ONE cleared 5, so this view rendered
             * "1 of 1 creative": arithmetically correct, and not a ranking. Fleet-wide, 38.7% of
             * zero-purchase accounts have three or fewer creatives clearing it against 20.9% of
             * purchase accounts. The adaptive form can never be STRICTER than 5, so no account
             * loses a row it has today, and it can never empty the view.
             *
             * The `2x target CPA` half is NOT here. It needs a spend threshold derived from the
             * brand's target, and prod holds a `cac` target for one brand of 35,118 — so the bound
             * would do nothing for everyone and silently narrow the view for one. The median floor
             * alone is what makes the CPA-ascending ranking trustworthy, which is what the floor was
             * for; the target multiple can join it when targets are actually set.
             */
            query: 'range=60d&sort=cpa&dir=asc&view=table&filters=eyJtIjoiYSIsInIiOlt7ImYiOiJyZXN1bHRzIiwibyI6Imd0ZUFkYXB0aXZlRmxvb3IiLCJ2Ijo1fSx7ImYiOiJzcGVuZCIsIm8iOiJndGVNZWRpYW4ifV19&columns=roas%2Ccpa%2Cresults%2Cspend%2Caov%2CconversionRate%2ChookRate%2CdaysActive',
        },
    },
    {
        name: 'Fatigued Ads',
        data: {
            schemaVersion: exports.DEFAULT_CREATIVE_PERFORMANCE_VIEW_SCHEMA_VERSION,
            /**
             * `days active >= 21 AND impressions >= 1,000 AND CPA RISING`, and then either
             * `CPA >= 2x its own past` or `outbound CTR fell by at least 15%`.
             *
             * THE CPA-RISING CONJUNCT IS LOAD-BEARING, and it is not in the original spec's wording —
             * it stands in for the spec's `frequency > 2.5`. The spec's second symptom is
             * `frequency > 2.5 AND CTR fell`; frequency needs de-duplicated reach, which exists for
             * ~3% of accounts, so dropping it does not weaken that arm — it REMOVES THE AND and lets
             * any CTR dip fire on its own. Measured on a live account over 7 days: 172 creatives
             * flagged, 53 of them with IMPROVING CPA. Requiring the cost to be rising restores the
             * "two independent signals agree" structure using a metric available fleet-wide: 57
             * flagged, none with improving CPA.
             *
             * The encoded form is the one-level rewrite, because a group has a single mode and
             * `A OR (B AND C)` needs two. Since `CPA >= 2x` implies `CPA rising`, the rule
             * `cpaDoubled OR (ctrFell AND cpaRose)` is EQUIVALENT to
             * `cpaRose AND (cpaDoubled OR ctrFell)` — verified against real data as the identical
             * 57-creative set.
             *
             * A creative with no purchases in the previous window has no cost trend to corroborate
             * with, so it is not flagged. That is the conservative reading and it matches how every
             * other metric here treats an absent comparison.
             *
             * `frequency` stays as a COLUMN: informative where the reach lane covers the account,
             * and harmless where it does not.
             */
            query: 'range=7d&view=table&filters=eyJtIjoiYSIsImciOlt7Im0iOiJhIiwiciI6W3siZiI6ImNwYUNoYW5nZSIsIm8iOiJndGUiLCJ2IjowLjAwMDF9LHsiZiI6ImRheXNBY3RpdmUiLCJvIjoiZ3RlIiwidiI6MjF9LHsiZiI6ImltcHJlc3Npb25zIiwibyI6Imd0ZSIsInYiOjEwMDB9XX0seyJtIjoibyIsInIiOlt7ImYiOiJjcGFWc093blBhc3QiLCJvIjoiZ3RlIiwidiI6Mn0seyJmIjoib3V0Ym91bmRDdHJDaGFuZ2UiLCJvIjoibHRlIiwidiI6LTAuMTV9XX1dfQ&columns=daysActive%2Cspend%2Cfrequency%2CoutboundCtrChange%2CcpmChange%2CcpaChange%2CcpaVsOwnPast',
        },
    },
    {
        name: 'Top Landing Pages',
        data: {
            schemaVersion: exports.DEFAULT_CREATIVE_PERFORMANCE_VIEW_SCHEMA_VERSION,
            query: 'range=30d&view=table&group=landingPage&groupOrderBy=spend&groupOrderDir=desc&columns=spend%2CadCount%2CoutboundClicks%2ClandingPageViewRate%2CconversionRate%2CrevenuePerVisitor%2CcostPerAddToCart%2Croas',
        },
    },
];
